import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { enUS, es, ptBR } from 'date-fns/locale';
import { dictionaries } from 'src/i18n/dictionaries';
import { translate } from 'src/i18n/translate';

const STORAGE_KEY = 'agilodonto.locale';

export const LOCALES = [
  { id: 'pt-BR', label: 'PT-BR' },
  { id: 'en', label: 'EN' },
  { id: 'es', label: 'ES' }
];

const DATE_LOCALES = {
  'pt-BR': ptBR,
  en: enUS,
  es
};

function readStoredLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return LOCALES.some((item) => item.id === saved) ? saved : 'pt-BR';
  } catch (error) {
    return 'pt-BR';
  }
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(readStoredLocale);

  const setLocale = (next) => {
    if (!LOCALES.some((item) => item.id === next)) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (error) {
      // ignore private-mode storage failures
    }
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => {
    const messages = dictionaries[locale] || dictionaries['pt-BR'];
    return {
      locale,
      setLocale,
      locales: LOCALES,
      messages,
      dateLocale: DATE_LOCALES[locale] || ptBR,
      t: (key, vars) => translate(messages, key, vars)
    };
  }, [locale]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

const fallbackMessages = dictionaries['pt-BR'];
const fallbackLocale = {
  locale: 'pt-BR',
  setLocale: () => {},
  locales: LOCALES,
  messages: fallbackMessages,
  dateLocale: ptBR,
  t: (key, vars) => translate(fallbackMessages, key, vars)
};

export function useLocale() {
  const context = useContext(LocaleContext);
  return context || fallbackLocale;
}
